import { Badge, Table } from 'react-bootstrap';
import { connect } from 'react-redux';
import Swal from 'sweetalert2';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { mainColors } from '../../helpers/colors';
import {
  deleteProjectActionRequest,
  setProjectsSortDataAction,
  sortProjectsAction,
} from '../../store/project/projectActions';
import { setAlertAction } from '../../store/alert/alertActions';
import useMercurxPagination from '../../hooks/useMercurxPagination';
import MercurXIcons from '../MercurXIcons/MercurXIcons';
import MercurXSortableHeader from '../MercurXSortableHeader/MercurXSortableHeader';
import MercurXTablePagination from '../MercurXTablePagination/MercurXTablePagination';
import {
  PROJECTS_SORT_TYPES,
  nftSortKeys,
  sortKeys,
} from '../../pages/Projects/projectsConstants';
import './MercurXProjectTable.scss';

function MercurXProjectTable(props) {
  const {
    deleteProjectRequest,
    setAlert,
    projects,
    filteredProjects,
    projectsSortData,
    setProjectsSortData,
    sortProjects,
  } = props;
  const history = useHistory();
  const location = useLocation();
  const user = JSON.parse(localStorage.getItem('user'))?.user;
  const isNftPage = location.pathname.includes('/nft-projects');
  const sourceData = Array.isArray(filteredProjects)
    ? filteredProjects
    : Array.isArray(projects)
      ? projects
      : [];
  const visibleProjects = sourceData.filter((item) => item?.status !== false);
  const activeSortKeys = projectsSortData?.sortKeys || (isNftPage ? nftSortKeys : sortKeys);
  const emptyMessage = isNftPage
    ? 'No NFT project found according to search results.'
    : 'No token project found according to search results.';
  const {
    currentPage,
    totalPages,
    visiblePages,
    pagedData: renderedProjects,
    rowOffset,
    isFirstPage,
    isLastPage,
    goToPage,
    goToFirstPage,
    goToPrevPage,
    goToNextPage,
    goToLastPage,
  } = useMercurxPagination(visibleProjects, 10, 5);

  function scrollToProjectsContainer() {
    const projectsContainer = document.getElementById('projects-container');
    if (projectsContainer) {
      projectsContainer.scrollIntoView({ behavior: 'smooth' });
    }
  }

  const handlePageChange = (page) => {
    goToPage(page);
    scrollToProjectsContainer();
  };

  const handleFirstPage = () => {
    goToFirstPage();
    scrollToProjectsContainer();
  };

  const handlePrevPage = () => {
    goToPrevPage();
    scrollToProjectsContainer();
  };

  const handleNextPage = () => {
    goToNextPage();
    scrollToProjectsContainer();
  };

  const handleLastPage = () => {
    goToLastPage();
    scrollToProjectsContainer();
  };

  const handleSort = (sortKey, defaultSortType) => {
    const nextSortType = projectsSortData.sortKey === sortKey
      ? (projectsSortData.sortType === PROJECTS_SORT_TYPES.ASCENDING
        ? PROJECTS_SORT_TYPES.DESCENDING
        : PROJECTS_SORT_TYPES.ASCENDING)
      : defaultSortType;

    setProjectsSortData({
      sortKey,
      sortType: nextSortType,
      sortKeys: activeSortKeys,
    });
    sortProjects();
  };

  function editProject(project) {
    const editProjectPath = location.pathname.includes('/nft-projects')
      ? '/nft-projects/edit-project'
      : '/token-projects/edit-project';

    history.push({
      pathname: editProjectPath,
      state: {
        project,
      },
    });
  }

  function deleteProject(projectID, projectIndex) {
    Swal.fire({
      icon: 'warning',
      iconColor: mainColors.warning,
      text: 'This process can not be undo. Are you sure?',
      showCancelButton: true,
    }).then((control) => {
      if (control.isConfirmed) {
        deleteProjectRequest({
          projectID,
          projectIndex,
        });
      }
    });
  }

  function formatUsd(value, minimumFractionDigits = 2) {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
      return '-';
    }

    return Number(value).toLocaleString('tr-TR', {
      style: 'currency',
      currency: 'USD',
      currencyDisplay: 'symbol',
      useGrouping: true,
      minimumFractionDigits,
    });
  }

  function calculateFormat(value, maximumFractionDigits = 2) {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
      return '-';
    }

    const formatBalance = (
      Math.floor((Number(value) / 10000) * 10 ** maximumFractionDigits) /
      10 ** maximumFractionDigits
    )
      .toString()
      .split('.');
    const firstValueFormatBalance = Number(formatBalance[0]).toLocaleString('tr-TR');
    const secondValueFormatBalance = formatBalance[1] || '0';
    return `${firstValueFormatBalance},${secondValueFormatBalance}`;
  }

  function formatNumber(value, maximumFractionDigits = 4) {
    if (value === null || value === undefined || value === '' || Number.isNaN(Number(value))) {
      return '-';
    }

    return Number(value).toLocaleString('tr-TR', {
      useGrouping: true,
      maximumFractionDigits,
    });
  }

  function shortenText(value, startLength = 8, endLength = 6) {
    if (!value) {
      return '-';
    }

    const text = String(value);
    if (text.length <= startLength + endLength + 3) {
      return text;
    }

    return `${text.slice(0, startLength)}...${text.slice(-endLength)}`;
  }

  async function copyToClipboard(text, label = 'Value') {
    if (!text || text === '-') {
      return;
    }

    const normalizedText = String(text);
    let isCopied = false;

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(normalizedText);
        isCopied = true;
      }
    } catch (error) {
      // fallback below
    }

    if (!isCopied) {
      const textArea = document.createElement('textarea');
      textArea.value = normalizedText;
      textArea.style.position = 'fixed';
      textArea.style.opacity = '0';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      isCopied = document.execCommand('copy');
      document.body.removeChild(textArea);
    }

    if (isCopied) {
      setAlert({
        title: 'Success!',
        text: `${label} Copied To Clipboard`,
        variant: 'success',
        outTimeMS: 3000,
      });
    }
  }

  function renderCopyableText(value, options = {}) {
    const { startLength = 8, endLength = 6, label = 'Value' } = options;
    if (!value) {
      return '-';
    }

    const text = String(value);
    return (
      <span
        role="button"
        tabIndex={0}
        className="copyable-cell-text"
        title={`${text} (Click to copy)`}
        onClick={() => copyToClipboard(text, label)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            copyToClipboard(text, label);
          }
        }}
      >
        {shortenText(text, startLength, endLength)}
      </span>
    );
  }

  return (
    <>
      <Table className="projects-table-design" responsive hover borderless>
        <thead>
          <tr className="text-t-body-color text-fs-tag">
            <th>#</th>
            {activeSortKeys.map((sortKeyConfig, index) => (
              <th key={sortKeyConfig.name}>
                <MercurXSortableHeader
                  label={sortKeyConfig.name}
                  isActive={projectsSortData.sortKey === index}
                  isAscending={projectsSortData.sortType === PROJECTS_SORT_TYPES.ASCENDING}
                  onClick={() => handleSort(index, sortKeyConfig.defaultSortType)}
                />
              </th>
            ))}
            {user?.is_superuser && (
              <>
                <th />
                <th />
              </>
            )}
          </tr>
        </thead>
        {renderedProjects?.length ? (
          <tbody>
            {renderedProjects.map((item, index) => {
              const tokenAsset = item?.token || {};
              const nftAsset = item?.nft || {};
              const asset = isNftPage ? nftAsset : tokenAsset;

              if (isNftPage) {
                return (
                  <tr className="text-t-head-color" key={item.id || index}>
                    <td>{rowOffset + index + 1}</td>
                    <td>{asset.name || '-'}</td>
                    <td>
                      {renderCopyableText(asset.url, {
                        startLength: 22,
                        endLength: 14,
                        label: 'NFT URL',
                      })}
                    </td>
                    <td>{renderCopyableText(asset.address, { label: 'NFT Address' })}</td>
                    <td>{formatNumber(asset.price_in_mercurx)}</td>
                    <td>{formatUsd(asset.price_in_usd, 4)}</td>
                    <td>{formatNumber(asset.distribution)}</td>
                    <td>{formatNumber(asset.total_supply)}</td>
                    <td>{formatNumber(asset.current_price)}</td>
                    <td>{formatNumber(asset.all_time_high)}</td>
                    <td>
                      {item.is_active === 'active' ? (
                        <Badge bg="success">Active</Badge>
                      ) : item.is_active === 'completed' ? (
                        <Badge bg="danger">Completed</Badge>
                      ) : (
                        <Badge bg="secondary">Other</Badge>
                      )}
                    </td>
                    {user?.is_superuser && (
                      <>
                        <td>
                          <div className="cursor-pointer" onClick={() => editProject(item)}>
                            <MercurXIcons
                              name="BsPencilSquare"
                              size={18}
                              color={mainColors.primary}
                            />
                          </div>
                        </td>
                        <td>
                          <div
                            className="cursor-pointer"
                            onClick={() => deleteProject(item.id, index)}
                          >
                            <MercurXIcons
                              name="BsTrash3"
                              size={18}
                              color={mainColors.danger}
                            />
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                );
              }

              return (
                <tr className="text-t-head-color" key={item.id || index}>
                  <td>{rowOffset + index + 1}</td>
                  <td>{item.name || '-'}</td>
                  <td>{asset.symbol || '-'}</td>
                  <td>{asset.price_in_mercurx ?? '-'}</td>
                  <td>{formatUsd(asset.price_in_usd, 4)}</td>
                  <td>{formatUsd(item.total_raised, 2)}</td>
                  <td>{formatUsd(item.target_raised, 2)}</td>
                  <td>{calculateFormat(item.total_tokens_sold)}</td>
                  <td>{calculateFormat(asset.distribution)}</td>
                  <td>{calculateFormat(asset.total_supply)}</td>
                  <td>
                    {item.is_active === 'active' ? (
                      <Badge bg="success">Active</Badge>
                    ) : item.is_active === 'completed' ? (
                      <Badge bg="danger">Completed</Badge>
                    ) : (
                      <Badge bg="secondary">Other</Badge>
                    )}
                  </td>
                  {user?.is_superuser && (
                    <>
                      <td>
                        <div className="cursor-pointer" onClick={() => editProject(item)}>
                          <MercurXIcons
                            name="BsPencilSquare"
                            size={18}
                            color={mainColors.primary}
                          />
                        </div>
                      </td>
                      <td>
                        <div
                          className="cursor-pointer"
                          onClick={() => deleteProject(item.id, index)}
                        >
                          <MercurXIcons name="BsTrash3" size={18} color={mainColors.danger} />
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        ) : null}
      </Table>

      {!renderedProjects?.length && (
        <div className="d-flex text-fs-body-md text-t-body-color justify-content-center">
          {emptyMessage}
        </div>
      )}

      <MercurXTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        visiblePages={visiblePages}
        isFirstPage={isFirstPage}
        isLastPage={isLastPage}
        onFirstPage={handleFirstPage}
        onPrevPage={handlePrevPage}
        onPageChange={handlePageChange}
        onNextPage={handleNextPage}
        onLastPage={handleLastPage}
      />
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    projects: state.projectReducer.projects,
    filteredProjects: state.projectReducer.filteredProjects,
    projectsSortData: state.projectReducer.projectsSortData,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    deleteProjectRequest: (payload) => {
      dispatch(deleteProjectActionRequest(payload));
    },
    setProjectsSortData: (payload) => {
      dispatch(setProjectsSortDataAction(payload));
    },
    sortProjects: (payload) => {
      dispatch(sortProjectsAction(payload));
    },
    setAlert: (payload) => {
      dispatch(setAlertAction(payload));
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MercurXProjectTable);
